import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'

import { useMemo, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/landing/section-heading'
import { ProductCard } from '@/components/landing/product-card'

const ALL = 'all'

interface CollectionSectionProps {
    products: Product[]
    onView: (product: Product) => void
}

export const CollectionSection: FC<CollectionSectionProps> = ({ products, onView }): ReactNode => {
    const [active, setActive] = useState<string>(ALL)

    const categories = useMemo(() => {
        const counts = new Map<string, number>()

        products.forEach((product) => counts.set(product.category, (counts.get(product.category) ?? 0) + 1))

        return Array.from(counts, ([name, count]) => ({ name, count }))
    }, [products])

    const current = active === ALL || categories.some((category) => category.name === active) ? active : ALL
    const visible = current === ALL ? products : products.filter((product) => product.category === current)

    const filters = [{ key: ALL, label: "All pieces", count: products.length }, ...categories.map((category) => ({ key: category.name, label: category.name, count: category.count }))]

    return (
        <section id='collection' aria-labelledby='collection-title' className='relative isolate overflow-hidden py-24 sm:py-32'>
            <div aria-hidden='true' className='absolute inset-0 -z-10'>
                <div className='ambient-orb -right-32 top-32 size-[30rem] bg-champagne/60' />
                <div className='ambient-orb -left-40 bottom-10 size-[26rem] bg-gold/20 [animation-delay:-11s]' />
            </div>

            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        id='collection-title'
                        eyebrow="The collection"
                        title={<>Autumn–Winter <em className='text-gilded'>2026</em></>}
                        description="Every piece is numbered and made in small editions. When a run is complete, it is never made again."
                    />

                    {products.length > 0 && (
                        <div
                            role='group'
                            aria-label="Filter the collection"
                            className='glass glass-clear no-scrollbar inline-flex max-w-full shrink-0 gap-1 self-start overflow-x-auto rounded-full p-1.5 lg:self-auto'
                        >
                            {filters.map((filter) => (
                                <button
                                    key={filter.key}
                                    type='button'
                                    aria-pressed={current === filter.key}
                                    onClick={() => setActive(filter.key)}
                                    className={cn(
                                        'flex h-10 items-center gap-2 whitespace-nowrap rounded-full px-4 text-[0.8rem] transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep',
                                        current === filter.key
                                            ? 'bg-espresso text-ivory shadow-[0_8px_20px_-10px_rgba(34,28,22,0.8)]'
                                            : 'text-espresso/70 hover:bg-white/60 hover:text-espresso'
                                    )}
                                >
                                    {filter.label}
                                    <span className={cn('text-[0.68rem] tabular-nums', current === filter.key ? 'text-champagne' : 'text-espresso/45')}>
                                        {filter.count}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {visible.length > 0 ? (
                    <ul className='mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                        {visible.map((product) => (
                            <li key={product.slug} className='animate-in fade-in slide-in-from-bottom-3 duration-700'>
                                <ProductCard product={product} onView={onView} />
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className='glass mx-auto mt-14 max-w-xl rounded-[2rem] p-10 text-center text-espresso'>
                        <p className='font-heading text-3xl'>
                            The new collection is being finished
                        </p>

                        <p className='mt-3 text-sm leading-relaxed text-muted-foreground'>
                            Our artisans are completing the next pieces by hand. Members of Le Cercle will see them first.
                        </p>

                        <a
                            href='#cercle'
                            className='mt-7 inline-flex h-12 items-center gap-2 rounded-full bg-espresso px-6 text-[0.75rem] uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-ink'
                        >
                            Apply to Le Cercle
                            <ArrowRight className='size-4' strokeWidth={1.5} />
                        </a>
                    </div>
                )}
            </div>
        </section>
    )
}
